import {
  MutableRefObject,
  ReactElement,
  useEffect,
  useMemo,
  useRef
} from "react";

import { HFlex } from "src/components/hFlex";
import { ClientFilters } from "src/components/tables/filters/clientFilters";
import { highlightSearchText } from "src/components/tables/filters/highlightSearchText";
import { useDebounce } from "src/hooks/useDebounce";
import { pluralize } from "src/utils/string";

import "./clientSideSearchBar.scss";

const CHANGE_TYPES = ["characterData", "childList"];

/**
 * Provides client-side row filtering and text highlighting for a specified
 * Type array.
 *
 * The hook handles two distinct concerns:
 *
 * 1. **Filtering** — a pure data transform. `filteredData` is derived from the
 *    input `data` array using the debounced query so the table only renders
 *    matching rows.
 *
 * 2. **Highlighting** — a DOM pass that runs after React commits. Because table
 *    cells are rendered by complex data-type classes (WildcardSwitchType,
 *    PortType, etc.) whose internals are opaque, we cannot inject highlight
 *    elements at render time. Instead a MutationObserver watches the scoped
 *    container ref and re-highlights whenever React adds or updates nodes.
 *
 * ## Usage
 * ```tsx
 * const { filteredData, textSearchBar, ref } = useTextSearch(tableData, filterCallback);
 *
 * return (
 *   <VFlex>
 *     {textSearchBar}
 *     <div ref={ref}>
 *       <TypedTable data={filteredData} ... />
 *     </div>
 *   </VFlex>
 * );
 * ```
 *
 * @param data - The full unfiltered array of data. Re-filtering runs automatically
 *               whenever this array or the debounced query changes.
 *
 * @param filterCallback - The function that will filter the date. It takes
 *                         the data array and the debounced query as arguments and
 *                         returns a boolean.
 *
 */
type TextSearchSchema<T> = {
  /**
   * The records after they are filtered
   */
  filteredData: T[];
  /**
   * A ref to be passed to the
   * component you want to filter
   * this will scope the HTML
   * to be targeted when highlighting
   * matches
   */
  ref: MutableRefObject<HTMLDivElement | null>;
  /**
   * The search bar to be inserted
   * so the user can input text to
   * be searched for.
   */
  textSearchBar: ReactElement;
};

type FilterCallback<T> = (item: T, query: string) => boolean;

export const useTextSearch = <T extends object>(
  data: T[] = [],
  filterCb: FilterCallback<T>
): TextSearchSchema<T> => {
  /**
   * Scopes the MutationObserver and the initial highlightNode call to the
   * element wrapping the table. Must be placed on a container that holds only
   * the table — not the search bar — so the TreeWalker does not scan input
   * labels or placeholder text.
   */
  const ref = useRef<HTMLDivElement | null>(null);
  const filter = useRef<FilterCallback<T>>(filterCb);
  filter.current = filterCb;

  /**
   * `query`         — the immediate value; drives the search input so every
   *                   keystroke is reflected without lag.
   * `debouncedQuery`— the debounced value (350 ms default); drives filtering
   *                   and highlighting to avoid thrashing on rapid input.
   * `setQuery`      — stable setter passed to the search input.
   */
  const [query, debouncedQuery, setQuery] = useDebounce("");

  /**
   * Derives the visible rows by testing every column value against the
   * debounced query. Each value is converted via `.toString()` — data-type
   * classes (WildcardSwitchType, PortType, etc.) override toString() to
   * return the human-readable text shown in the cell, so the filter matches
   * exactly what the user sees.
   */
  const filteredData = useMemo(() => {
    const searchFilter = debouncedQuery?.toLowerCase().trim();
    return data?.filter((r) => filter.current(r, searchFilter)) ?? [];
  }, [data, debouncedQuery]);

  /**
   * Human-readable result count shown beside the search bar while a query is
   * active (e.g. "3 records match"). Returns null when the search bar is empty
   * so no count is rendered in the default state.
   */
  const matches = useMemo(() => {
    const match = filteredData.length;
    if (debouncedQuery) {
      return pluralize(
        match,
        "1 record matches",
        `${match} records match`,
        "No records match"
      );
    }
    return null;
  }, [filteredData.length, debouncedQuery]);

  /**
   * Memoized on `query` (not `debouncedQuery`) so the input reflects every
   * keystroke immediately, keeping the search bar responsive while filtering
   * and highlighting are intentionally debounced.
   */
  const textSearchBar = useMemo(() => {
    return (
      <HFlex className="client-side-search-bar" align="center">
        <ClientFilters
          filters={[
            {
              type: "text",
              value: query,
              setValue: setQuery,
              placeholder: "Search",
              name: "search",
              label: ""
            }
          ]}
        />
        {matches}
      </HFlex>
    );
  }, [query, setQuery, matches]);

  useEffect(() => {
    const mutationObserver = new MutationObserver(
      (mutationList: MutationRecord[], observer: MutationObserver) => {
        if (ref.current) {
          for (const mutation of mutationList) {
            if (CHANGE_TYPES.includes(mutation.type)) {
              /** Take records should clear out any additional mutations records */
              observer.takeRecords();
              highlightSearchText(debouncedQuery, ref.current);
              break;
            }
          }
        }
      }
    );

    if (ref.current) {
      // Apply highlights to the current DOM before starting observation so
      // the initial render is covered even if no mutations fire.
      highlightSearchText(debouncedQuery.toLowerCase().trim(), ref.current);
      mutationObserver.observe(ref.current, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, [filteredData, debouncedQuery]);

  return {
    filteredData,
    textSearchBar,
    ref
  };
};
